import React, { useState } from 'react';
import { Label } from '../../../../../components/Form/Label/Label';
import { Grid } from '../../../../../components/Grid/Grid';
import { AsyncSearch } from '../../../../../components/Form/AsyncSearch/AsyncSearch';
import { Product, getProductVariantPrice } from '../../../../../model/product';
import { t } from '../../../../../locales/translate';
import { sortBy } from 'lodash';
import { TextField } from '../../../../../components/Form/TextField/TextField';
import { Select } from '../../../../../components/Form/Select/Select';
import { useChanges, Form } from '../../../../../components/Form/Form';
import { getDocumentFieldOptions } from '../../../../../model/document';
import { Order } from '../../../../../model/order';
import { handleError, handleSuccess } from '../../../../../model/notification';
import { Address } from '../../../../../model/company';
import { addItemToOrder, AddProductItem } from '../../../../../model/item';
import { deleteDelivery } from '../../../../../model/delivery';
import { AddItemsFormFooter, createDocuments, checkDocumentOption } from './AddItemsForm';
import { getStorageLocations } from '../../../../../model/storageLocation';
import { useRemoteData } from '../../../../../model/data';
import { ScrollWrapper } from '../../../../../components/ScrollWrapper/ScrollWrapper';
import { Stack } from '../../../../../components/Stack/Stack';
import { Spacer } from '../../../../../components/Spacer/Spacer';

interface Props {
  order: Order;
  preferedAddresses: {
    delivery: Address;
    invoice: Address;
  };
  onClose: () => void;
  onChange: () => void;
}

function canSubmit(model: AddProductItem): boolean {
  // Check if quantity is more than 0
  const amount = model.count && model.count > 0;
  // Check if both documents are selected
  const documentSelected = !!(model.deliveryId !== 'no' || model.invoiceId !== 'no');

  return !!amount && documentSelected;
}

export function IncomingItemsForm({ order, preferedAddresses, onClose, onChange }: Props): JSX.Element {
  const [createAnother, setCreateAnother] = useState(false);
  const [, setChanges, model] = useChanges<AddProductItem>({});
  const [loading, setLoading] = useState(false);
  const [storageLocations, loadingStorageLocations, errorStorageLocations] = useRemoteData(
    getStorageLocations,
    (db) => db.table('storageLocations').all
  );

  // Delivery Options
  const deliveryOptions = getDocumentFieldOptions(order.deliveries, 'delivery');

  function setProduct(product?: Product): void {
    if (!product) {
      const { deliveryId, storageLocationId } = model;

      return setChanges({
        deliveryId,
        storageLocationId,
      });
    }

    const variant = product.productVariants?.[0];

    if (variant) {
      setChanges({
        ...model,
        product,
        variantId: variant.id,
        count: 1,
        price: getProductVariantPrice(variant),
        deliveryId: model.deliveryId ?? deliveryOptions[0].value,
        storageLocationId: model.storageLocationId ?? 'no',
      });
    }
  }

  function handleOnInput(newModel: AddProductItem, field: string): void {
    if (field === 'variantId') {
      const productVariant = model.product?.productVariants?.find((variant) => {
        return variant.id === newModel.variantId;
      });

      // Prefill price for additional products
      newModel.price = productVariant && getProductVariantPrice(productVariant);
    }
    setChanges(newModel);
  }

  async function handleSubmit(): Promise<void> {
    setLoading(true);

    // Create documents
    const [createdDelivery] = await createDocuments({
      orderId: order.id,
      delivery: model.deliveryId === 'new',
      preferedAddresses,
    });

    const incomingDeliveryId = createdDelivery?.id || checkDocumentOption(model.deliveryId);

    try {
      await addItemToOrder(order.id, {
        productId: model.product?.id,
        price: model.price,
        count: Number(model.count),
        productVariantId: model.variantId || '',
        incomingDeliveryId,
        storageId: checkDocumentOption(model.storageLocationId),
      });

      if (!createAnother) {
        onClose();
      } else {
        handleSuccess(t.success.addedItems(t.model.item._name));
        setChanges({
          ...model,
          deliveryId: incomingDeliveryId ?? model.deliveryId ?? deliveryOptions[0].value,
        });
      }
      onChange();
    } catch (e) {
      handleError(e);
      // If add item fails we need to clear the documents:
      if (createdDelivery) {
        deleteDelivery(order.id, createdDelivery.id);
      }
    }

    setLoading(false);
  }

  return (
    <>
      <ScrollWrapper className="add-items-modal__content">
        <Form model={model} onInput={handleOnInput}>
          <Stack>
            {/* Select Product */}
            <AsyncSearch
              label={`${t.model.order.incomingProducts}`}
              name="productId"
              searchIn={['product']}
              onSelect={(item) => {
                setProduct(item as Product);
              }}
              maxResults={20}
              required
            />
            {model.product && (
              <>
                {/* Select Variant */}
                <Select
                  name="variantId"
                  label={`${t.model.category._name} / ${t.model.productVariant._name}`}
                  options={
                    model.product.productVariants && [
                      { value: '', label: '' },
                      ...sortBy(model.product.productVariants, ['order']).map((variant) => ({
                        value: variant.id,
                        label: variant.description,
                      })),
                    ]
                  }
                  required
                />
                {model.variantId && (
                  <>
                    <TextField label={t.model.item.quantity} name="count" type="number" autoFocus required />
                  </>
                )}
                <Spacer size="none" />
                {/* Select Invoice and Delivery */}
                <Label title={`${t.model.invoice._name} / ${t.model.delivery._name}`} />
                <Grid>
                  <Select
                    style={{ marginBottom: 'var(--spacing-md)' }}
                    name="deliveryId"
                    label={t.model.delivery._name}
                    required
                    options={deliveryOptions}
                  />
                  {!errorStorageLocations ? (
                    <Select
                      style={{ marginBottom: 'var(--spacing-md)' }}
                      name="storageLocationId"
                      label={t.model.order.storageLocation}
                      required
                      options={[
                        {
                          value: 'no',
                          label: t.model.order.noStorageLocation,
                        },
                        ...storageLocations.map((location) => ({
                          value: location.id,
                          label: location.name,
                        })),
                      ]}
                      loading={loadingStorageLocations}
                    />
                  ) : (
                    <div>{t.errors.errorLoadingItem(t.model.order.storageLocation)}</div>
                  )}
                </Grid>
              </>
            )}
          </Stack>
        </Form>
      </ScrollWrapper>
      <AddItemsFormFooter
        loading={loading}
        onClose={onClose}
        createAnother={createAnother}
        onChangeCreateAnother={() => setCreateAnother(!createAnother)}
        handleSubmit={handleSubmit}
        canSubmit={canSubmit(model)}
      ></AddItemsFormFooter>
    </>
  );
}
